import { SearchOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import {
  Checkbox,
  ConfigProvider,
  Drawer,
  Form,
  Input,
  Modal,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  IGetRouteDetail,
  IUpdateRouteDetail,
  QUERY_ADD_CUSTOM_SALELINE,
  QUERY_REMOVE_CUSTOMER,
  QUERY_UPDATE_INDEX_ROUTE,
  VARIABLES_UPDATE_INDEX_ROUTE,
  VARIABLE_ADD_CUSTOM_SALELINE,
  VARIABLE_REMOVE_CUSTOMER,
} from 'apis/GraphQL/Queries/saleLineQuery';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { SaleLineApi } from 'apis/SaleLineApi';
import { EditIcon, XGrayIcon, XIcon } from 'assets/svg';
import { Button } from 'components';
import ButtonAction from 'components/Button/ButtonAction';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { Rows } from 'components/Table/Row';
import TableMoveRow, { DragIndex } from 'components/Table/TableMoveRow';
import { UploadModal } from 'components/UploadModal_V2';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import { toast } from 'react-toastify';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import formatNumber, {
  formatCurrency,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_CUSTOMER_INTO_ROUTE } from 'utils/constants/importId';
import ModalAddCustomerToRoute from '../SalePlan/ModalAddCustomerToRoute/ModalAddCustomerToRoute';

const { confirm } = Modal;
const { SaveOutlined, PlusOutlined } = icons;

interface Iprops {
  setOpen: any;
  open: boolean;
  recordId: any;
  children?: ReactNode;
}

const findRouteDetail = (item: any, list: any[]) => {
  return list.find(
    (route) => route.id === item.id && route.customerId === item.customerId,
  );
};

const dayCheckboxList = [
  {
    title: 'T2',
    dataIndex: 't2',
  },
  {
    title: 'T3',
    dataIndex: 't3',
  },
  {
    title: 'T4',
    dataIndex: 't4',
  },
  {
    title: 'T5',
    dataIndex: 't5',
  },
  {
    title: 'T6',
    dataIndex: 't6',
  },
  {
    title: 'T7',
    dataIndex: 't7',
  },
  {
    title: 'CN',
    dataIndex: 'cn',
  },
];

const weekCheckboxList = [
  {
    title: 'Tuần 1',
    dataIndex: 'week1',
  },
  {
    title: 'Tuần 2',
    dataIndex: 'week2',
  },
  {
    title: 'Tuần 3',
    dataIndex: 'week3',
  },
  {
    title: 'Tuần 4',
    dataIndex: 'week4',
  },
];

const dayIndexes = dayCheckboxList.map((item) => item.dataIndex);
const weekIndexes = weekCheckboxList.map((item) => item.dataIndex);

const DrawerSaleLinePage = ({ setOpen, open, recordId }: Iprops) => {
  const { DragHandle } = Rows,
    [form] = Form.useForm();

  const [page, setPage] = useState(1),
    [data, setData] = useState<any>([]),
    [pageSize, setPageSize] = useState(10),
    [searchValue, setSearchValue] = useState(''),
    [dataDetail, setDataDetail] = useState<any>([]);

  const [isEdit, setIsEdit] = useState(false),
    [openAddCustomer, setOpenAddCustomer] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const searchDebounce = useDebounce(searchValue, 500);

  const scroll = useMemo(() => {
    return { x: 875 };
  }, []);

  const tablePagination = useMemo(() => {
    return {
      current: page,
      pageSize: pageSize,
      total: data?.length || 0,
      onChange: (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
      },
    };
  }, [page, pageSize, data]);

  const mutationUpdateRouteIndex = useMutation({
    mutationKey: ['getUpdateRouteIndex'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      res.data.updateManyRouteDetail && toastSuccess('Cập nhật thành công');
    },
    onError: (error) => {
      console.log(error);
      toastError('Cập nhật thất bại');
    },
  });

  const mutationUpdateRouteDetail = useMutation({
    mutationKey: ['getUpdateRouteDetail'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getRouteDetail, isPending: isGetRouteDetailPending } =
      useMutation({
        mutationFn: (params: IGetRouteDetail) =>
          SaleLineApi.getRouteDetail(params),
        onSuccess: (res: any) => {
          if (res.data?.getRouteDetail) {
            setData(res.data.getRouteDetail?.items ?? []);
            return;
          }

          res?.errors.length > 0 &&
            toastError(`Lỗi. ${res.errors[0]?.message}!`);
        },
        onError: (error) => {
          toastError(`Lỗi. ${error?.message ?? error}!`);
        },
      }),
    {
      mutate: updateManyRouteDetailMutate,
      isPending: isUpdateManyRouteDetailPending,
    } = useMutation({
      mutationFn: (params: IUpdateRouteDetail[]) =>
        SaleLineApi.updateManyRouteDetail(params),
      onSuccess: (res) => {
        if (res.data?.updateManyRouteDetail) {
          toastSuccess('Cập nhật thành công!');
          refetchRouteDetail();
        }
      },
      onError: (error) => {
        toastError(`Cập nhật thất bại. ${error?.message ?? error}!`);
      },
    });

  const mutationAddCustomerToRoute = useMutation({
    mutationKey: ['getAddCustomerToRoute'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      res.data.createManyRouteDetail &&
        toastSuccess('Thêm khách hàng thành công');

      handleAddCustomerSuccess();
      setOpenAddCustomer(false);
    },
    onError: (error) => {
      console.log(error);
      toastError('Thêm khách hàng thất bại');
    },
  });

  const mutationRemoveCustomerToRoute = useMutation({
    mutationKey: ['getAddCustomerToRoute'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      res.data.updateRouteDetail && toastSuccess('Xóa khách hàng thành công');

      getRouteDetail({
        id: recordId,
        ...(searchDebounce && { searchValue: searchDebounce.trim() }),
      });

      setOpenAddCustomer(false);
    },
    onError: (error) => {
      console.log(error);
      toastError('Xóa khách hàng thất bại');
    },
  });

  useEffect(() => {
    setSearchValue('');

    if (!recordId || !open) return;

    setPage(1);
    setPageSize(10);
    getRouteDetail({ id: recordId });
  }, [open, recordId]);

  useEffect(() => {
    isEdit || setDataDetail([]);
  }, [isEdit, setDataDetail]);

  useEffect(() => {
    open || setIsEdit(false);
  }, [open]);

  useEffect(() => {
    refetchRouteDetail();
  }, [searchDebounce]);

  const handleOnChange = (value: any, record: any, fieldName: string) => {
    const valueWithoutDots = value.replace(/\./g, '').replace(',', '.');
    const valueNumber = Number(valueWithoutDots);
    const valueWithDots = formatNumber(valueNumber);

    if (valueNumber < 0) {
      toast.dismiss();
      toastError('Doanh thu trung bình không được âm!');
    } else if (!/^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/.test(valueWithDots)) {
      toast.dismiss();
      toastError('Doanh thu trung bình chỉ được nhập số!');
    }

    if (isNaN(valueWithoutDots)) {
      form.setFieldValue(fieldName, value);
      return;
    }

    form.setFieldValue(fieldName, valueWithDots);
  };

  const handleExpectedRevenueFocus = useCallback(
    (record: any) => {
      if (!record || !record?.id) return;

      if (dataDetail.length <= 0) {
        setDataDetail([record]);
        return;
      }

      setDataDetail((prev: any[]) => {
        let isExist = false;
        const oldData = prev.map((item: any) => {
          if (item.id === record.id) {
            isExist = true;
            return item;
          }
          return item;
        });

        if (!isExist) oldData.push(record);

        return oldData;
      });
    },
    [dataDetail],
  );

  const columns: ColumnsTypeCustom = useMemo(() => {
    return [
      {
        className: 'shadow-right',
        width: 120,
        fixed: 'left',
        align: 'center',
        render: (_: any, record: any) => (
          <Space size={'middle'} className="text-lg ">
            <DragHandle
              title={'Di chuyển'}
              disabled={isEdit || !(data && data.length > 1)}
            />
            <ButtonAction
              tooltip="Xóa"
              disabled={isEdit}
              onClick={() =>
                confirm({
                  okText: 'Xác nhận',
                  cancelText: 'Huỷ',
                  title: `Bạn có muốn xoá khách hàng ${record?.customer?.storeName}`,
                  onOk() {
                    handleRemoveCustomer(record.id);
                  },
                })
              }
            >
              <XIcon />
            </ButtonAction>
          </Space>
        ),
      },
      {
        width: 50,
        title: 'STT',
        align: 'center',
        dataIndex: 'stt',
        render: (_: any, __: any, index: number) =>
          (page - 1) * pageSize + index + 1,
      },
      {
        width: 150,
        align: 'left',
        title: 'Mã khách hàng',
        dataIndex: ['customer', 'sapCustomerId'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 150,
        align: 'left',
        title: 'Mã DMS',
        dataIndex: ['customer', 'id'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 220,
        align: 'left',
        title: 'Tên Cửa Hàng',
        dataIndex: ['customer', 'storeName'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 100,
        align: 'left',
        title: 'Hạng thẻ',
        dataIndex: ['customer', 'trank', 'name'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 180,
        align: 'right',
        key: 'storeName',
        currency: 'vi-VN',
        dataIndex: 'expectedRevenue',
        title: 'Doanh thu trung bình',
        render: (value: any, record: any) => {
          const isValidValue = value !== null && value !== undefined;
          const valueFormatted = isValidValue ? formatNumber(value) : value;
          const fieldName = `expectedRevenue_${record?.id}`;

          return isEdit ? (
            <Form.Item
              name={fieldName}
              className="m-0"
              initialValue={valueFormatted}
              rules={[
                {
                  pattern: /^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/,
                  message: '',
                },
              ]}
            >
              <Input
                value={valueFormatted}
                className="w-full"
                onChange={(e) =>
                  handleOnChange(e.target.value, record, fieldName)
                }
                onFocus={() => handleExpectedRevenueFocus(record)}
              />
            </Form.Item>
          ) : isValidValue ? (
            formatCurrency({
              value,
              round: true,
            })
          ) : (
            '-'
          );
        },
      },
      {
        width: 120,
        title: 'MST',
        dataIndex: ['customer', 'taxId'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 150,
        title: 'SDT',
        align: 'left',
        dataIndex: ['customer', 'phone'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 150,
        title: 'CCCD',
        align: 'left',
        dataIndex: ['customer', 'cccd'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 150,
        align: 'left',
        title: 'Địa chỉ chi tiết',
        dataIndex: ['customer', 'address'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 140,
        align: 'left',
        title: 'Tỉnh/Thành Phố',
        dataIndex: ['customer', 'tcity', 'name'],
        render: (value: any) => (value ? value : '-'),
      },

      {
        width: 120,
        align: 'left',
        title: 'Phường/Xã',
        dataIndex: ['customer', 'twrad', 'name'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        title: 'Tần suất đi trong tháng',
        children: weekCheckboxList.map((item) => ({
          title: item.title,
          dataIndex: item.dataIndex,
          align: 'center',
          width: 63,
          render: (value: any, record: any) => {
            const find = findRouteDetail(record, dataDetail);

            return (
              <Form.Item className="mb-0">
                <Checkbox
                  checked={find?.[item.dataIndex] ?? value}
                  onChange={(e) =>
                    onChangeFrequency(record, item.dataIndex, e.target.checked)
                  }
                />
              </Form.Item>
            );
          },
        })),
      },
      ...dayCheckboxList.map(
        (item) =>
          ({
            title: item.title,
            dataIndex: item.dataIndex,
            align: 'center',
            width: 40,
            render: (value: any, record: any) => {
              const find = findRouteDetail(record, dataDetail);

              return (
                <Form.Item className="mb-0">
                  <Checkbox
                    checked={find?.[item.dataIndex] ?? value}
                    onChange={(e) =>
                      onChangeFrequency(
                        record,
                        item.dataIndex,
                        e.target.checked,
                      )
                    }
                  />
                </Form.Item>
              );
            },
          }) as any,
      ),
      {
        width: 140,
        align: 'center',
        title: 'Người tạo',
        dataIndex: ['tcreateUser', 'fullName'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 180,
        align: 'center',
        title: 'Thời gian tạo',
        dataIndex: 'createDate',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        width: 140,
        align: 'center',
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
        render: (value: any) => (value ? value : '-'),
      },
      {
        width: 180,
        align: 'center',
        dataIndex: 'modifyDate',
        title: 'Thời gian cập nhật',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
    ];
  }, [isEdit, data, dataDetail, page, pageSize]);

  function checkWeeklyFrequency(record: any) {
    if (weekIndexes.some((item) => record[item])) return true;
    toastError('Chọn ít nhất 1 tuần');
    return false;
  }

  function checkDaylyFrequency(record: any, dataIndex: any) {
    if (weekIndexes.some((item) => item !== dataIndex && record[item]))
      return true;
    if (dayIndexes.some((item) => record[item])) {
      toastError('Đang chọn 1 ngày trong tuần');
      return false;
    }
    return true;
  }

  function onChangeFrequency(
    record: any,
    dataIndex: any,
    checked: boolean,
    fieldValue?: string | number,
  ) {
    if (!isEdit) return;

    const find = findRouteDetail(record, dataDetail) ?? {};
    const newRecord = { ...record, ...find };

    const isWeekValidate =
      checked ||
      dayIndexes.includes(dataIndex) ||
      checkDaylyFrequency(newRecord, dataIndex);

    const isDayValidate =
      !checked ||
      weekIndexes.includes(dataIndex) ||
      checkWeeklyFrequency(newRecord);

    if (isDayValidate && isWeekValidate) {
      setDataDetail((prev: any[]) => {
        let isExist = false;
        const value = fieldValue ?? checked;

        const newData = prev.map((item) => {
          if (item.id === record.id && item.customerId === record.customerId) {
            isExist = true;
            return { ...item, [dataIndex]: value };
          }

          return item;
        });

        if (!isExist) {
          newData.push({
            id: record.id,
            customerId: record.customerId,
            [dataIndex]: value,
          });
        }

        return newData;
      });
    }
  }

  const onClose = () => {
    setOpen(false);
  };

  const handleChangeRow = useCallback(
    (dragRow: DragIndex) => {
      const { activeIndex, overIndex } = dragRow;

      if (
        overIndex !== -1 &&
        activeIndex != undefined &&
        overIndex != undefined &&
        activeIndex != overIndex
      )
        mutationUpdateRouteIndex.mutate({
          query: QUERY_UPDATE_INDEX_ROUTE,
          variables: VARIABLES_UPDATE_INDEX_ROUTE([
            { id: data?.[activeIndex].id, indexNum: overIndex + 1 },
            { id: data?.[overIndex].id, indexNum: activeIndex + 1 },
          ]),
        });
    },
    [data],
  );

  async function handleOK(data: any) {
    await mutationAddCustomerToRoute.mutateAsync({
      query: QUERY_ADD_CUSTOM_SALELINE,
      variables: VARIABLE_ADD_CUSTOM_SALELINE(recordId, data),
    });
  }

  const handleRemoveCustomer = (customerId: string) => {
    mutationRemoveCustomerToRoute.mutate({
      query: QUERY_REMOVE_CUSTOMER,
      variables: VARIABLE_REMOVE_CUSTOMER(customerId),
    });
  };

  const refetchRouteDetail = () => {
    if (!recordId) return;
    const value = searchDebounce.trim();

    getRouteDetail({
      id: recordId,
      ...(value && { searchValue: value }),
    });
  };

  const handleImportClick = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const handleClickEditBtn = async () => {
    try {
      if (!isEdit) {
        setIsEdit(true);
        form.resetFields();
        return;
      }

      if (!(await form.validateFields())) return;

      if (dataDetail.length <= 0) {
        setIsEdit(false);
        return;
      }

      const formValues = form.getFieldsValue();

      const expectedRevenueValues = Object.keys(form.getFieldsValue()).reduce(
        (acc, key) => {
          const [_, id] = key.split('_');

          if (!formValues[key]) {
            acc[id] = 0;
            return acc;
          }

          const valueWithoutDots = formValues[key]
            .replace(/\./g, '')
            .replace(',', '.');

          acc[id] = Number(valueWithoutDots);

          return acc;
        },
        {} as any,
      );

      updateManyRouteDetailMutate(
        dataDetail.map((item: any) => {
          const { id, customerId, ...others } = item;

          return { id, ...others, expectedRevenue: expectedRevenueValues[id] };
        }),
      );

      setIsEdit(false);
      form.resetFields();
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickAddBtn = () => {
    if (isEdit) {
      setIsEdit(false);
      form.resetFields();
    } else setOpenAddCustomer(true);
  };

  const handleExportExcel = () => {
    const schedule = [...dayCheckboxList, ...weekCheckboxList].map(
      (item) => item.dataIndex,
    );

    const newData = [...data].map((item: any) => {
      return {
        ...item,
        ...schedule.reduce((acc: any, key: string) => {
          return {
            ...acc,
            [key]: item[key] ? 1 : 0, // FIXME Tần suất
          };
        }, {}),
      };
    });

    exportExcel_v2(columns, newData, 'Khách hàng trong tuyến');
  };

  const handleAddCustomerSuccess = useCallback(() => {
    if (searchValue !== '') {
      setSearchValue('');
      return;
    }

    refetchRouteDetail();
  }, [searchValue, refetchRouteDetail]);

  return (
    <Drawer
      destroyOnClose
      open={open}
      width={'60%'}
      closable={false}
      onClose={onClose}
      title={
        <Typography.Text className="text-[#003056] font-bold text-2xl">
          Khách hàng trong tuyến
        </Typography.Text>
      }
      extra={<ButtonAction title={<XGrayIcon />} onClick={onClose} />}
    >
      <Space className="flex mb-3 justify-end mr-5">
        <ConfigProvider
          theme={{
            token: {
              borderRadius: 50,
            },
          }}
        >
          <Input
            allowClear
            value={searchValue}
            className="p-2 w-64 bg-[#EFF3F8]"
            placeholder="Tìm khách hàng theo tên, mã"
            prefix={<SearchOutlined className="mx-1" />}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </ConfigProvider>
        <ButtonExport disabled={isEdit} onClick={handleExportExcel} />
        <ButtonImport disabled={isEdit} onClick={handleImportClick} />
        <Button
          customClass="bg-white"
          disabled={!data?.length}
          title={
            isEdit ? <SaveOutlined style={{ fontSize: 20 }} /> : <EditIcon />
          }
          handleOnclick={handleClickEditBtn}
        />
        <Button
          customClass="bg-white"
          title={isEdit ? <XIcon /> : <PlusOutlined />}
          handleOnclick={handleClickAddBtn}
        />
      </Space>
      <Form form={form}>
        <TableMoveRow
          rowKey="id"
          dataSource={data}
          columns={columns}
          scroll={scroll}
          loading={
            isGetRouteDetailPending ||
            mutationUpdateRouteIndex.isPending ||
            mutationUpdateRouteDetail.isPending ||
            isUpdateManyRouteDetailPending
          }
          pagination={tablePagination}
          setDataSource={setData}
          onChangeRow={handleChangeRow}
        />
      </Form>
      <UploadModal
        importId={IMPORT_CUSTOMER_INTO_ROUTE}
        templateFileName="Tuyến bán hàng - Khách hàng trong tuyến - Template"
        open={isOpenImportModal}
        title={'Thêm mới khách hàng vào tuyến'}
        setOpen={setIsOpenImportModal}
        refetch={handleAddCustomerSuccess}
      />
      <ModalAddCustomerToRoute
        type="SaleLine"
        open={openAddCustomer}
        searchId={recordId}
        handleOK={handleOK}
        setOpen={setOpenAddCustomer}
      />
    </Drawer>
  );
};

export default DrawerSaleLinePage;
